import { FC, useEffect, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { AlexandriaCandidate } from 'api/models/AlexandriaCandidate';
import { useDebouncedValue } from 'hooks';
import { useAlexandriaCompanyProfile, useAlexandriaCompanySearch } from 'services/hooks/dailyNav';
import { formatProviderCode } from 'utilities';
import { AlexandriaCompanyPickerProps } from './AlexandriaCompanyPicker.props';
import { ALEXANDRIA_COMPANY_PICKER_TEST_IDS } from './AlexandriaCompanyPicker.testIds';

// Search once typing pauses, rather than making the user discover Enter.
const SEARCH_DEBOUNCE_MS = 500;
const MIN_SEARCH_LENGTH = 2;

// '' (cleared) and undefined both mean "search with the company's own name".
const toSubmittedTerm = (term?: string) => {
  const trimmed = term?.trim() ?? '';
  return trimmed.length ? trimmed : undefined;
};

const AlexandriaCompanyPicker: FC<AlexandriaCompanyPickerProps> = ({
  firmId,
  companyId,
  selectedAlexandriaId,
  onChange,
  disabled,
}) => {
  // Undefined = search with the company's own name/website (the backend default).
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
  const [submittedTerm, setSubmittedTerm] = useState<string | undefined>(undefined);
  const [showSearch, setShowSearch] = useState(!selectedAlexandriaId);
  const debouncedTerm = useDebouncedValue(searchTerm, SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    if (debouncedTerm === undefined) {
      return;
    }
    const trimmed = debouncedTerm.trim();
    // A single character matches too broadly to be worth an upstream call.
    if (trimmed.length && trimmed.length < MIN_SEARCH_LENGTH) {
      return;
    }
    setSubmittedTerm(toSubmittedTerm(debouncedTerm));
  }, [debouncedTerm]);

  const {
    data: searchData,
    isFetching: searchLoading,
    isError: searchFailed,
  } = useAlexandriaCompanySearch({
    firmId,
    companyId,
    name: submittedTerm,
    // Read-only viewers are allowed to search but can't act on a result, so
    // don't spend an upstream Alexandria call on them.
    enabled: showSearch && !disabled,
  });

  // Asked about the draft ID, not the saved one, so picking a result relabels
  // immediately rather than a config save later.
  const {
    data: profile,
    isFetching: profileLoading,
    isError: profileFailed,
  } = useAlexandriaCompanyProfile({ firmId, companyId, alexandriaId: selectedAlexandriaId });

  useEffect(() => {
    if (!selectedAlexandriaId) {
      setShowSearch(true);
    }
  }, [selectedAlexandriaId]);

  const results: AlexandriaCandidate[] = searchData?.results ?? [];
  const hasSearched = !searchLoading && searchData !== undefined;
  const noResults = hasSearched && results.length === 0;

  // The profile is for whichever ID is linked right now; while a newly picked one
  // is still loading, the previous company's name would be a lie.
  const currentProfile = profile?.alexandria_id === selectedAlexandriaId ? profile : undefined;
  const resolvedName = currentProfile?.is_resolved ? currentProfile.display_name : '';
  const providers = currentProfile?.providers ?? [];

  const handleSelect = (candidate: AlexandriaCandidate) => {
    onChange(candidate.alexandria_id);
    setShowSearch(false);
  };

  const handleRemove = () => {
    onChange(null);
    setShowSearch(true);
  };

  // Enter still searches straight away, without waiting for the pause.
  const handleSearch = () => setSubmittedTerm(toSubmittedTerm(searchTerm));

  const renderProviders = () => {
    if (profileLoading && !currentProfile) {
      return <Skeleton variant="text" width={220} data-testid={ALEXANDRIA_COMPANY_PICKER_TEST_IDS.providersLoading} />;
    }
    if (profileFailed) {
      // The link itself is fine; only the label could not be fetched.
      return (
        <Typography
          variant="caption"
          color="text.secondary"
          data-testid={ALEXANDRIA_COMPANY_PICKER_TEST_IDS.profileUnavailable}>
          Could not reach the market-data service, so this profile&apos;s name and providers are unavailable right now.
        </Typography>
      );
    }
    if (currentProfile && !currentProfile.is_resolved) {
      return (
        <Alert severity="warning" sx={{ mt: 1 }} data-testid={ALEXANDRIA_COMPANY_PICKER_TEST_IDS.profileUnresolved}>
          This external company profile is no longer recognized. Daily NAV prices will not resolve until the company
          is relinked.
        </Alert>
      );
    }
    if (!currentProfile) {
      return null;
    }
    if (providers.length === 0) {
      return (
        <Typography
          variant="caption"
          color="text.secondary"
          data-testid={ALEXANDRIA_COMPANY_PICKER_TEST_IDS.noProviders}>
          No data providers are mapped to this profile yet.
        </Typography>
      );
    }
    return (
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}
        data-testid={ALEXANDRIA_COMPANY_PICKER_TEST_IDS.providers}>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
          Data providers:
        </Typography>
        {providers.map(provider => (
          <Tooltip
            key={`${provider.provider_code}-${provider.provider_company_id}`}
            title={
              provider.provider_company_id
                ? `${formatProviderCode(provider.provider_code)} ID ${provider.provider_company_id}`
                : ''
            }>
            <Chip
              label={formatProviderCode(provider.provider_code)}
              size="small"
              variant="outlined"
              data-testid={ALEXANDRIA_COMPANY_PICKER_TEST_IDS.providerChip(provider.provider_code)}
            />
          </Tooltip>
        ))}
      </Box>
    );
  };

  return (
    <Box>
      {selectedAlexandriaId ? (
        <Box sx={{ mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Falls back to the raw ID whenever the name is not (yet) known —
                the link is still real, and hiding it would hide the link. */}
            <Chip
              id="alexandria-linked-chip"
              label={resolvedName || selectedAlexandriaId}
              size="small"
              onDelete={disabled ? undefined : handleRemove}
              disabled={disabled}
            />
            {profileLoading && !currentProfile && <CircularProgress size={14} />}
            {!showSearch && (
              <Button
                id="alexandria-change-button"
                size="small"
                variant="text"
                onClick={() => setShowSearch(true)}
                disabled={disabled}
                sx={{ minWidth: 0 }}>
                Change
              </Button>
            )}
          </Box>
          {/* Only once the chip is showing a NAME: the ID is what the config
              stores and what a support conversation about a bad match refers to,
              so it stays visible — but when the name is unknown the chip is
              already the ID, and repeating it reads as two different facts.
              Labelled without naming the upstream provider, which is ours to
              know rather than the user's. */}
          {resolvedName && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.5 }}
              data-testid={ALEXANDRIA_COMPANY_PICKER_TEST_IDS.alexandriaId}>
              Scalar External Company Profile ID {selectedAlexandriaId}
            </Typography>
          )}
          <Box sx={{ mt: 0.5 }}>{renderProviders()}</Box>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Not linked to an external company profile.
        </Typography>
      )}

      {showSearch && (
        <>
          <TextField
            id="alexandria-search-input"
            placeholder="Search by company name…"
            size="small"
            fullWidth
            value={searchTerm ?? ''}
            onChange={e => setSearchTerm(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSearch();
              }
            }}
            disabled={disabled}
            helperText="Searches as you type, starting from this company's name and website. Names are matched literally, so try a legal or alternate name if there is no hit."
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchLoading ? (
                  <InputAdornment position="end">
                    <CircularProgress size={16} />
                  </InputAdornment>
                ) : null,
              },
            }}
          />

          {searchFailed && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Could not reach the market-data service. Try again in a moment.
            </Alert>
          )}

          {!searchFailed && results.length > 0 && (
            <Paper variant="outlined" sx={{ mt: 0.5, maxHeight: 200, overflow: 'auto' }}>
              <List dense disablePadding>
                {results.map(candidate => (
                  <ListItemButton
                    key={candidate.alexandria_id}
                    onClick={() => handleSelect(candidate)}
                    disabled={disabled}>
                    <ListItemText primary={candidate.display_name || candidate.alexandria_id} />
                    {candidate.match_type === AlexandriaCandidate.match_type.DOMAIN && (
                      <Chip label="domain match" size="small" color="info" variant="outlined" />
                    )}
                  </ListItemButton>
                ))}
              </List>
            </Paper>
          )}

          {!searchFailed && noResults && (
            <Alert severity="info" sx={{ mt: 1 }}>
              No match in external company database. Try the company&apos;s legal or alternate name.
            </Alert>
          )}
        </>
      )}
    </Box>
  );
};

export default AlexandriaCompanyPicker;
